/** SSE hook for a task's processing progress: its files, the derivation of its elements, the novelty run, the suggestion run, the claim set proposal and the framework proposal. */

import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { BASE_URL } from "@/services/api-client";
import type {
  AnalysisResponse,
  ClaimStrategyResponse,
  FrameworkResponse,
  InventionElementsResponse,
  PositionsResponse,
  TaskResponse,
} from "@patent-studio/api-types";
import {
  extractionHasSettled,
  parseFileStatusEvent,
  parseInventionExtractionEvent,
  parseNoveltyAnalysisEvent,
  parseFrameworkProposalEvent,
  parseProposalEvent,
  parseSuggestionEvent,
  proposalHasSettled,
  suggestionHasSettled,
} from "@/lib/sse-validators";
import { TERMINAL_PROCESSING_STATUSES } from "@/lib/constants";
import {
  claimStrategyKey,
  ddFrameworkKey,
  figuresKey,
  inventionElementsKey,
  noveltyAnalysisKey,
  noveltyGateScope,
  positionsKey,
  notificationsKey,
  questionsKey,
  taskDetailKey,
  thesisGateScopes,
} from "./query-keys";
import { useSseSubscription } from "./use-sse-subscription";

/**
 * The novelty run's motion, applied to the cache the tab reads.
 *
 * Patched while it runs, so the tab counts cells done of total as they land;
 * the verdicts are read once, in one piece, when the run settles. A landing is
 * not patched as landed: its counts and verdicts arrive only with that read,
 * and "ready" over the counts still cached would draw the pre-run numbers as
 * the run's until it answers. So the bar fills and the status waits for the
 * read. A failed run lands nothing, so its status and reason are patched as
 * they come. The gate carries the run's state, so every move of the run marks
 * it stale. A settled run also moves the strips each position's confirmation
 * is read from, so the positions are read again with the verdicts.
 */
function applyAnalysisEvent(
  queryClient: QueryClient,
  taskId: string,
  data: string,
): void {
  const event = parseNoveltyAnalysisEvent(data);
  if (!event) {
    console.warn("Ignoring invalid novelty analysis SSE event:", data);
    return;
  }
  const key = noveltyAnalysisKey(taskId);
  const landed = event.status === "ready";
  queryClient.setQueryData<AnalysisResponse>(key, (prev) =>
    prev
      ? {
          ...prev,
          status: landed ? prev.status : event.status,
          progress: event.progress,
          error: landed ? prev.error : (event.error ?? null),
        }
      : prev,
  );
  void queryClient.invalidateQueries({ queryKey: noveltyGateScope(taskId) });
  if (extractionHasSettled(event.status)) {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: positionsKey(taskId) });
    void queryClient.invalidateQueries({ queryKey: claimStrategyKey(taskId) });
  }
}

/**
 * The derivation pass's motion, applied to the cache the Invention page reads.
 *
 * Patched rather than refetched while it runs, so the page can say the documents
 * are being read the moment the pass starts. The elements land in one merge, so a
 * settled pass is the cue to read the set: the same terminal-only rule this hook
 * already applies to the task.
 */
function applyExtractionEvent(
  queryClient: QueryClient,
  taskId: string,
  data: string,
): void {
  const event = parseInventionExtractionEvent(data);
  if (!event) {
    console.warn("Ignoring invalid invention extraction SSE event:", data);
    return;
  }
  const key = inventionElementsKey(taskId);
  queryClient.setQueryData<InventionElementsResponse>(key, (prev) =>
    prev
      ? { ...prev, extraction: event.status, extraction_error: event.error }
      : prev,
  );
  if (extractionHasSettled(event.status)) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * The suggestion run's motion, applied to the set the card reads.
 *
 * Patched while the worker writes, so the card reads busy from the ask to the
 * landing; the positions arrive in one write under the set's version, and a
 * settled status is the cue to read the set again, with the gate and the
 * analysis, which both follow the list.
 */
function applySuggestionEvent(
  queryClient: QueryClient,
  taskId: string,
  data: string,
): void {
  const event = parseSuggestionEvent(data);
  if (!event) {
    console.warn("Ignoring invalid position suggestion SSE event:", data);
    return;
  }
  const key = positionsKey(taskId);
  queryClient.setQueryData<PositionsResponse>(key, (prev) =>
    prev ? { ...prev, suggestion: event } : prev,
  );
  if (suggestionHasSettled(event.status)) {
    void queryClient.invalidateQueries({ queryKey: key });
    for (const queryKey of thesisGateScopes(taskId)) {
      void queryClient.invalidateQueries({ queryKey });
    }
    void queryClient.invalidateQueries({
      queryKey: noveltyAnalysisKey(taskId),
    });
  }
}

/**
 * The claim set proposal run's motion, applied to the stage the page reads.
 *
 * Patched while the worker writes, keeping which run it is, so the page reads
 * busy from the ask to the landing; a settled status is the cue to read the
 * stage again. A landing is not patched: its claims arrive only with that read,
 * and a complete run over the claims still cached would draw every claim as
 * removed until it answers.
 */
function applyProposalEvent(
  queryClient: QueryClient,
  taskId: string,
  data: string,
): void {
  const event = parseProposalEvent(data);
  if (!event) {
    console.warn("Ignoring invalid claim proposal SSE event:", data);
    return;
  }
  const key = claimStrategyKey(taskId);
  if (event.status === "complete") {
    void queryClient.invalidateQueries({ queryKey: key });
    return;
  }
  // The run id is how the stream tells two runs apart; the page reads the run it is served.
  const { run_id: _run, ...moved } = event;
  queryClient.setQueryData<ClaimStrategyResponse>(key, (prev) =>
    prev
      ? {
          ...prev,
          proposal: {
            ...prev.proposal,
            ...moved,
            // The stream repeats the stored run on every connect, so only a moved
            // status says the run is live again; a stranded run stays stale.
            stale: event.status === prev.proposal.status && prev.proposal.stale,
          },
        }
      : prev,
  );
  if (proposalHasSettled(event.status)) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * Praxis's framework proposal run, applied to the framework the Specification page reads.
 *
 * Patched while the worker writes, so the page reads busy from the ask to the landing; a
 * settled run is the cue to read the framework again, which is how its sections arrive.
 */
function applyFrameworkProposalEvent(
  queryClient: QueryClient,
  taskId: string,
  data: string,
): void {
  const event = parseFrameworkProposalEvent(data);
  if (!event) {
    console.warn("Ignoring invalid framework proposal SSE event:", data);
    return;
  }
  const key = ddFrameworkKey(taskId);
  queryClient.setQueryData<FrameworkResponse>(key, (prev) =>
    prev ? { ...prev, proposal: { ...prev.proposal, ...event } } : prev,
  );
  if (proposalHasSettled(event.status)) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * A queue of questions moved: an ask landed its questions, ended without any,
 * or the card cleared it. The event is the cue to read the questions again.
 */
function applyQuestionQueuesEvent(
  queryClient: QueryClient,
  taskId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: questionsKey(taskId) });
}

function applyNotificationsEvent(
  queryClient: QueryClient,
  taskId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: notificationsKey(taskId) });
}

/**
 * Which handler each named event on the stream is applied by.
 *
 * The task's own files are not here: a file status patches a different query and
 * carries the only rule that is its own. Everything else is one entry, so a fifth
 * status added to the stream is a line rather than another branch.
 *
 * A Map rather than an object, so only the names registered here answer. An
 * object also answers for every name Object.prototype carries, and while those
 * members do nothing when called with these arguments, a lookup that can return
 * something nobody registered is not a table.
 */
const APPLY = new Map<
  string,
  (queryClient: QueryClient, taskId: string, data: string) => void
>([
  ["invention_extraction", applyExtractionEvent],
  ["analysis", applyAnalysisEvent],
  ["position_suggestion", applySuggestionEvent],
  ["claim_proposal", applyProposalEvent],
  ["dd_framework_proposal", applyFrameworkProposalEvent],
  ["question_queues", applyQuestionQueuesEvent],
  ["notifications", applyNotificationsEvent],
]);

/**
 * Subscribe to a task's processing progress via SSE.
 *
 * When the worker updates a file's status (e.g. textract_pending → indexed),
 * the Change Stream fires and this hook patches the TanStack Query cache
 * so the UI updates in real-time without polling.
 *
 * The derivation of the elements and the novelty run ride the same stream and
 * are dispatched here rather than from hooks of their own: one change stream
 * over the task document already sees all three, because each status is a
 * field on the same document the file statuses sit on.
 */
export function useFileProcessing(taskId: string | null) {
  const queryClient = useQueryClient();

  const onMessage = useCallback(
    (data: string, name: string) => {
      if (!taskId) return;
      const apply = APPLY.get(name);
      if (apply !== undefined) {
        apply(queryClient, taskId, data);
        return;
      }
      if (name !== "file_status") return;
      const event = parseFileStatusEvent(data);
      if (!event) {
        console.warn("Ignoring invalid file status SSE event:", data);
        return;
      }

      queryClient.setQueryData<TaskResponse>(taskDetailKey(taskId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          files: prev.files.map((f) =>
            f.file_id === event.file_id ? { ...f, ...event } : f,
          ),
        };
      });

      // Reading a file changes more of the task than the file itself. The
      // worker writes the disclosure text and names an unnamed draft, and
      // neither travels on this event, so the header would keep the empty
      // title it loaded with until the page was navigated. Only on a
      // terminal status: a refetch mid-processing is the race described in
      // ``use-files.ts``, where the REST snapshot lands after SSE has
      // already moved the cache on.
      if (TERMINAL_PROCESSING_STATUSES.has(event.processing_status)) {
        void queryClient.invalidateQueries({ queryKey: taskDetailKey(taskId) });
        // Clips are served on the figures read, so a transcript that just
        // finished only reaches the Define card through this refetch.
        void queryClient.invalidateQueries({ queryKey: figuresKey(taskId) });
      }
    },
    [taskId, queryClient],
  );

  useSseSubscription({
    url: taskId ? `${BASE_URL}/tasks/${taskId}/files/stream` : null,
    onMessage,
  });
}
